import { isIsolated } from './algebra.ts';
import { defaultLessonPackage, getStageLesson, lesson } from './lesson.ts';
import type {
  AssessmentCriterion,
  LessonContent,
  LessonPackage,
  LearningSession,
  StageId,
  StageProgress,
  UnderstandingBand,
  WorkshopAssessmentSummary,
} from './types.ts';

export const ASSESSMENT_VERSION = defaultLessonPackage.assessment.version;

const learningAttemptKinds = new Set([
  'operation',
  'candidate',
  'unknown',
  'model',
  'explanation',
  'unit',
]);

function hasWorkEvidence(
  session: LearningSession,
  stageId: StageId,
  progress: StageProgress,
  content: LessonContent,
): boolean {
  const expected = getStageLesson(session, stageId, content).expectedSolution;
  return (
    (progress.history.length > 0 && isIsolated(progress.equation, expected)) ||
    (progress.workingEquations ?? []).some(
      (line) => line.equivalent && isIsolated(line.equation, expected),
    )
  );
}

function candidateMet(progress: StageProgress): boolean {
  return (
    progress.candidateCheck?.correct === true &&
    progress.candidateCheck.substitutionCorrect
  );
}

function modelMet(progress: StageProgress): boolean {
  return (
    progress.modelAnswer?.correct === true ||
    progress.modelEquation?.correct === true
  );
}

function criterion(
  id: string,
  stageId: StageId,
  label: string,
  met: boolean,
): AssessmentCriterion {
  return { id, stageId, label, met };
}

function stageCriteria(
  session: LearningSession,
  stageId: StageId,
  content: LessonContent,
): AssessmentCriterion[] {
  const progress = session.stages[stageId];
  const result = [
    criterion(
      `${stageId}-work`,
      stageId,
      'Record an isolated solution',
      hasWorkEvidence(session, stageId, progress, content),
    ),
    criterion(
      `${stageId}-check`,
      stageId,
      'Check the value in the original equation',
      candidateMet(progress),
    ),
    criterion(
      `${stageId}-explanation`,
      stageId,
      'Choose a correct explanation of the reasoning',
      progress.explanationAnswer?.correct === true,
    ),
  ];
  if (stageId === 'concept') return result;
  return [
    criterion(
      `${stageId}-unknown`,
      stageId,
      'Identify what the unknown represents',
      progress.unknownAnswer?.correct === true,
    ),
    criterion(
      `${stageId}-model`,
      stageId,
      'Build or choose a correct story model',
      modelMet(progress),
    ),
    result[0],
    result[1],
    criterion(
      `${stageId}-unit`,
      stageId,
      'Interpret the solution with the correct unit',
      progress.unitAnswer?.correct === true,
    ),
    result[2],
  ];
}

function understandingBand(
  earned: number,
  total: number,
): { band: UnderstandingBand; label: string } {
  if (earned === total)
    return { band: 'high', label: 'High demonstrated understanding' };
  if (earned * 2 >= total)
    return { band: 'showing', label: 'Showing understanding' };
  if (earned > 0)
    return { band: 'building', label: 'Building understanding' };
  return { band: 'starting', label: 'Ready to begin' };
}

export function assessmentFor(
  session: LearningSession,
  content: LessonContent = lesson,
): WorkshopAssessmentSummary {
  const stageIds: StageId[] = ['concept', 'application', 'transfer'];
  const checklist = stageIds.flatMap((stageId) =>
    stageCriteria(session, stageId, content),
  );
  const earnedCriteria = checklist.filter((item) => item.met).length;
  const totalCriteria = checklist.length;
  const score = Math.round((earnedCriteria / totalCriteria) * 100);
  const { band, label } = understandingBand(earnedCriteria, totalCriteria);
  const activities = stageIds.map((stageId) => {
    const progress = session.stages[stageId];
    const attempted = (value: StageProgress) =>
      value.attempts.some((attempt) =>
        learningAttemptKinds.has(attempt.kind),
      );
    const tried =
      progress.complete ||
      attempted(progress) ||
      (stageId === 'transfer' &&
        (session.transferArchives ?? []).some((archive) =>
          attempted(archive.progress),
        ));
    return {
      stageId,
      label: content.stages[stageId].title,
      tried,
      complete: progress.complete,
    };
  });
  const tried = activities.filter((activity) => activity.tried).length;
  const allTried = tried === activities.length;
  const stagesComplete = activities.every((activity) => activity.complete);
  const eligibleForCompletion =
    band === 'high' && allTried && stagesComplete;
  const missing = checklist
    .filter((item) => !item.met)
    .map((item) => `${content.stages[item.stageId].title}: ${item.label}.`);
  if (!allTried)
    for (const activity of activities.filter((item) => !item.tried))
      missing.push(`${activity.label}: try one checked activity step.`);

  return {
    version:
      'assessment' in content
        ? (content as LessonPackage).assessment.version
        : ASSESSMENT_VERSION,
    understanding: {
      score,
      band,
      label,
      earnedCriteria,
      totalCriteria,
      note: 'Checked rubric coverage from this lesson; not a probability or calibrated confidence. An equivalent isolated line records the solution but does not prove how it was derived.',
    },
    coverage: {
      tried,
      total: activities.length,
      allTried,
      activities,
    },
    checklist,
    eligibleForCompletion,
    missing,
  };
}
